import { useEffect, useState } from "react";
import { api } from "./api";
import { FOUNDER as FALLBACK_FOUNDER, LEADERSHIP_TEAM as FALLBACK_TEAM } from "../data/teamData";

/**
 * useTeamData() — fetches the live admin-managed team-members list.
 *
 * Falls back to the static teamData.js export if the API hasn't loaded yet
 * (first render) or fails — so /founder and /team never go blank.
 *
 * Returns { founder, leadership, all, loading }.
 */
export function useTeamData() {
    const [items, setItems] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const { data } = await api.get("/team-members");
                if (cancelled) return;
                setItems(data.items || []);
            } catch {
                if (!cancelled) setItems(null);  // signal fallback
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, []);

    // Use API data when available, otherwise fall back to static
    if (items && items.length) {
        const founder = items.find((m) => m.is_founder) || items[0];
        const leadership = items.filter((m) => m.id !== founder.id);
        return { founder, leadership, all: items, loading, source: "api" };
    }
    return {
        founder: {
            id: "fallback-founder",
            slug: "anand-rajput",
            name: FALLBACK_FOUNDER.name,
            role: FALLBACK_FOUNDER.role,
            bio: FALLBACK_FOUNDER.bio[0],
            quote: FALLBACK_FOUNDER.quote,
            education: FALLBACK_FOUNDER.education,
            achievements: FALLBACK_FOUNDER.achievements,
            responsibilities: FALLBACK_FOUNDER.expertise,
            photo_asset_key: "founder_photo",
            is_founder: true,
        },
        leadership: FALLBACK_TEAM.map((m, idx) => ({
            id: `fallback-${m.slug}`,
            slug: m.slug,
            name: m.name,
            role: m.role,
            bio: m.bio,
            responsibilities: m.expertise,
            photo_asset_key: `team_photo_${m.slug}`,
            is_founder: false,
            display_order: idx + 1,
        })),
        all: [],
        loading,
        source: "fallback",
    };
}
